import { AppComplexAutocomplete } from '@forms/Components/AutocompleteInput';
import { AppMultiSelectChipComplex } from '@forms/Components/MultiSelectChipInput';
import { AutocompleteInputChangeReason } from '@mui/material';
import { useMemo, useState } from 'react';
import { blankLookupItem, getLookupLabel, isBlankLookupItem, LookupItem, LookupListProps, minimumSearchLength } from './types';
import styles from '../Page.module.css';

const isLookupOptionEqual = (option: unknown, candidate: unknown) => (option as LookupItem).id === (candidate as LookupItem).id;

/** Two lines per option so the name can be recognized while the ID that gets saved stays visible. */
const renderLookupOption = ({ key, ...optionProps }: React.HTMLAttributes<HTMLLIElement> & { key: unknown }, option: unknown) => {
  const item = option as LookupItem;

  return (
    <li
      key={String(key)}
      {...optionProps}
      className={`${optionProps.className ?? ''} ${styles['lookup-option']}`}>
      <span className={styles['lookup-name']}>{item.name}</span>
      {/* The blank option stands for the absence of a record, so it has no ID line to sit under its name */}
      {!isBlankLookupItem(item) && <span className={styles['lookup-id']}>{item.id}</span>}
      {item.detail && <span className={styles['lookup-detail']}>{item.detail}</span>}
    </li>
  );
};

/**
 * The picker itself. Both lookups render through this; only the search behind it differs.
 *
 * The chosen records are held here rather than derived from the IDs, because the option list is replaced by every
 * search — deriving would strip the label off a selection as soon as the next search ran.
 */
export default function LookupList({ label, multiple, canBeBlank, value, onChange, items, isLoading, onSearch }: LookupListProps) {
  const [selected, setSelected] = useState<LookupItem[]>([]);
  const [inputText, setInputText] = useState('');

  // The parent owns the IDs, so a reset there (adding the filter) has to clear the records held here
  const chosen = useMemo(
    () =>
      value.map((id) =>
        id === blankLookupItem.id ? blankLookupItem : (selected.find((item) => item.id === id) ?? { id, name: id })
      ),
    [value, selected]
  );

  /** What an empty result reads as — on the blank row where there is one, and as MUI's own text where there is not */
  const emptyText = inputText.length < minimumSearchLength ? `Type at least ${String(minimumSearchLength)} characters` : 'No Matches';

  /**
   * Blank leads the list and survives every search, because it is the one choice no search can turn up. Only the
   * single picker offers it: "is one of" compares against a list of real codes, which absence is not one of. And only
   * where the field takes blank at all — offering it otherwise would set an operator that field's list does not hold.
   *
   * Where it is offered the list is never empty, which retires MUI's noOptionsText, so the row carries that text
   * itself when a search has turned nothing up.
   */
  const options = useMemo(() => {
    if (multiple || !canBeBlank) return items;
    if (items.length > 0) return [blankLookupItem, ...items];

    return [{ ...blankLookupItem, detail: emptyText }];
  }, [multiple, canBeBlank, items, emptyText]);

  const commit = (picked: LookupItem[]) => {
    setSelected(picked);
    onChange(picked.map((item) => item.id));
  };

  /** Shared so the single and multi pickers cannot drift apart — a missing onInputChange leaves the search dead. */
  const shared = {
    autoCompleteComplexItems: options,
    getOptionLabel: getLookupLabel,
    isOptionEqualToValue: isLookupOptionEqual,
    renderOption: renderLookupOption,
    // The search is already a startsWith against the server, so filtering the results again would only hide rows
    filterOptions: (options: unknown[]) => options,
    onInputChange: (text: string, reason: AutocompleteInputChangeReason) => {
      setInputText(text);
      // MUI replays the picked option's label through here after a selection. Searching for "Name (ID)" would only
      // fire a request that matches nothing and empty the option list, so react to typing alone.
      if (reason !== 'input') return;
      onSearch(text);
    },
    isLoading,
    noOptionsText: emptyText,
    placeholder: 'Search by name or ID',
    popperClass: styles['lookup-popper'],
    customProps: { labelText: label }
  };

  if (multiple) {
    return (
      <AppMultiSelectChipComplex
        {...shared}
        // The option list is the last search, so a selection made against an earlier one is no longer there to toggle off
        deletableChips
        value={chosen}
        onValueChange={(next) => {
          commit((next ?? []) as LookupItem[]);
        }}
      />
    );
  }

  return (
    <AppComplexAutocomplete
      {...shared}
      value={chosen[0] ?? null}
      onValueChange={(next) => {
        commit(next ? [next as LookupItem] : []);
      }}
    />
  );
}
